'use client';

import { useState, useEffect } from 'react';
import MethodologyHero from './MethodologyHero';
import ProcessFlow from './ProcessFlow';
import MethodologyStats from './MethodologyStats';
import DownloadFramework from './DownloadFramework';

interface Step {
  number: number;
  title: string;
  description: string;
  details: string[];
  icon: string;
  example: string;
}

interface Stat {
  value: string;
  label: string;
  icon: string;
}

export default function MethodologyInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroData = {
    title: "The Vibe Loop Methodology",
    subtitle: "Systematic Innovation Framework",
    description: "A proven systematic approach that transforms abstract market signals into concrete, buildable solutions. This methodology bridges the gap between visionary thinking and practical execution, ensuring every innovation is both breakthrough and deliverable."
  };

  const processSteps: Step[] = [
    {
      number: 1,
      title: "Market Vibe Detection",
      description: "Decode emerging market signals and identify untapped opportunities through deep pattern recognition and trend analysis.",
      details: [
        "Analyze industry pain points and unmet customer needs",
        "Study emerging technology trends and convergence points",
        "Identify market gaps where innovation can create significant value",
        "Map competitive landscape and differentiation opportunities"
      ],
      icon: "MagnifyingGlassIcon",
      example: "For a healthcare entreprise, detected the convergence of AI diagnostics and telemedicine, identifying a gap in real-time patient monitoring that led to a breakthrough IoT solution."
    },
    {
      number: 2,
      title: "Technical Feasibility Analysis",
      description: "Evaluate technological possibilities and constraints to ensure innovations are grounded in buildable reality.",
      details: [
        "Assess current technology stack capabilities and limitations",
        "Prototype core technical concepts to validate feasibility",
        "Identify required resources, skills, and infrastructure",
        "Map technical risks and mitigation strategies"
      ],
      icon: "BeakerIcon",
      example: "Revised a cryptography algorithm by succeffully reverse engineering it and then building proof-of-concept prototypes, while discovering and avoiding vulnerability paths that made the encryption bullet proof."
    },
    {
      number: 3,
      title: "Strategic Architecture Design",
      description: "Create comprehensive system blueprints that balance innovation ambition with practical implementation constraints.",
      details: [
        "Design scalable architecture that supports future growth",
        "Define clear technical specifications and requirements",
        "Establish development milestones and success metrics",
        "Create intellectual property protection strategy"
      ],
      icon: "CubeTransparentIcon",
      example: "Architected a modular AI simulation platform that enabled a robotics startup to launch MVP in 3 months while maintaining flexibility for future product testing."
    },
    {
      number: 4,
      title: "Rapid Prototype Development",
      description: "Build functional prototypes that demonstrate core value propositions and validate key assumptions quickly.",
      details: [
        "Develop minimum viable product with essential features",
        "Implement iterative testing and refinement cycles",
        "Gather early user feedback and usage data",
        "Optimize performance and user experience"
      ],
      icon: "RocketLaunchIcon",
      example: "Created working prototype of voice Assisted AI app in 6 weeks, securing seed funding and validating product-market fit."
    },
    {
      number: 5,
      title: "Market Validation & Iteration",
      description: "Test solutions with real users, gather actionable insights, and refine based on market feedback.",
      details: [
        "Conduct user testing sessions and collect feedback",
        "Analyze usage patterns and performance metrics",
        "Identify improvement opportunities and prioritize features",
        "Validate business model and revenue assumptions"
      ],
      icon: "ChartBarIcon",
      example: "Architected a B2B2C GTM with early adopters for an Oil & Gas customer achieving a +70% retention rate and creating a product distribution platform to around 2M users."
    },
    {
      number: 6,
      title: "Scale & Optimize",
      description: "Transition from prototype to production-ready solution with focus on scalability, reliability, and market expansion.",
      details: [
        "Optimize infrastructure for scale and performance",
        "Implement robust monitoring and analytics systems",
        "Establish operational processes and support systems",
        "Plan strategic growth and market expansion"
      ],
      icon: "ArrowTrendingUpIcon",
      example: "Orchestrated a simulated scaling up of an IoT platform from 1,000 to 100,000 connected devices while maintaining 99.9% uptime and reducing operational costs by 35%."
    }
  ];

  const stats: Stat[] = [
    {
      value: "10+",
      label: "Successful Implementations",
      icon: "CheckBadgeIcon"
    },
    {
      value: "40%",
      label: "Faster Time-to-Market",
      icon: "ClockIcon"
    },
    {
      value: "92%",
      label: "Client Success Rate",
      icon: "TrophyIcon"
    }
  ];

  const frameworkData = {
    title: "Get The Complete Framework",
    description: "Download the comprehensive Vibe Loop Methodology guide with detailed templates, checklists, and real-world case studies to implement this systematic approach in your own innovation projects.",
    benefits: [
      "Step-by-step templates",
      "Real case studies",
      "Implementation checklist"
    ]
  };

  return (
    <div data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:147:4" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="147" data-component-file="MethodologyInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <MethodologyHero data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:148:6" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="148" data-component-file="MethodologyInteractive.tsx" data-component-name="MethodologyHero" data-component-content="%7B%22elementName%22%3A%22MethodologyHero%22%7D" 
        title={heroData.title}
        subtitle={heroData.subtitle}
        description={heroData.description}
      />

      <section data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:154:6" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="154" data-component-file="MethodologyInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20md%3Apy-24%22%7D" className="py-16 md:py-24">
        <div data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:155:8" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="155" data-component-file="MethodologyInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%22%7D" className="max-w-7xl mx-auto px-4">
          <div data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:156:10" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="156" data-component-file="MethodologyInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
            <h2 data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:157:12" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="157" data-component-file="MethodologyInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-bold%20mb-4%20quantum-glow%22%2C%22textContent%22%3A%22Six-Phase%20Innovation%20Process%22%7D" className="text-3xl md:text-4xl font-bold mb-4 quantum-glow">
              Six-Phase Innovation Process
            </h2>
            <p data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:160:12" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="160" data-component-file="MethodologyInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Each%20phase%20builds%20upon%20the%20previous%2C%20creating%20a%20systematic%20approach%20that%20transforms%20abstract%20ideas%20into%20market-ready%20solutions.%22%7D" className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Each phase builds upon the previous, creating a systematic approach that transforms abstract ideas into market-ready solutions.
            </p>
          </div>

          <ProcessFlow data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:165:10" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="165" data-component-file="MethodologyInteractive.tsx" data-component-name="ProcessFlow" data-component-content="%7B%22elementName%22%3A%22ProcessFlow%22%7D" steps={processSteps} />
        </div>
      </section>

      <MethodologyStats data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:169:6" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="169" data-component-file="MethodologyInteractive.tsx" data-component-name="MethodologyStats" data-component-content="%7B%22elementName%22%3A%22MethodologyStats%22%7D" stats={stats} />

      <DownloadFramework data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:171:6" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="171" data-component-file="MethodologyInteractive.tsx" data-component-name="DownloadFramework" data-component-content="%7B%22elementName%22%3A%22DownloadFramework%22%7D" 
        title={frameworkData.title}
        description={frameworkData.description}
        benefits={frameworkData.benefits}
      />

      <section data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:177:6" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="177" data-component-file="MethodologyInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20md%3Apy-24%20bg-gradient-to-b%20from-background%20to-card%22%7D" className="py-16 md:py-24 bg-gradient-to-b from-background to-card">
        <div data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:178:8" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="178" data-component-file="MethodologyInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20px-4%20text-center%22%7D" className="max-w-4xl mx-auto px-4 text-center">
          <h2 data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:179:10" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="179" data-component-file="MethodologyInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20md%3Atext-3xl%20font-bold%20mb-6%20quantum-glow%22%2C%22textContent%22%3A%22Ready%20to%20Apply%20This%20Methodology%3F%22%7D" className="text-2xl md:text-3xl font-bold mb-6 quantum-glow">
            Ready to Apply This Methodology?
          </h2>
          <p data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:182:10" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="182" data-component-file="MethodologyInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20mb-8%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Let's%20discuss%20how%20The%20Vibe%20Loop%20can%20transform%20your%20innovation%20challenges%20into%20breakthrough%20solutions.%22%7D" className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let's discuss how The Vibe Loop can transform your innovation challenges into breakthrough solutions.
          </p>
          <a data-component-id="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx:185:10" data-component-path="src/app/the-vibe-loop-methodology/components/MethodologyInteractive.tsx" data-component-line="185" data-component-file="MethodologyInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%2Fcollaboration-gateway%22%2C%22className%22%3A%22inline-block%20px-8%20py-4%20bg-energy-orange%20text-background%20font-semibold%20rounded-lg%20hover%3Abg-energy-orange%2F90%20transition-all%20duration-300%20shadow-premium%20hover%3Ashadow-premium-lg%20transform%20hover%3Ascale-105%22%2C%22textContent%22%3A%22Book%20Strategy%20Call%22%7D"
            href="/collaboration-gateway"
            className="inline-block px-8 py-4 bg-energy-orange text-background font-semibold rounded-lg hover:bg-energy-orange/90 transition-all duration-300 shadow-premium hover:shadow-premium-lg transform hover:scale-105"
          >
            Book Strategy Call
          </a>
        </div>
      </section>
    </div>
  );
}